import React, {
  FC,
  ReactNode,
  SetStateAction,
  useEffect,
  useState,
} from "react";
import { Button } from "../Components/ui/button";
import {
  Activity,
  Check,
  DollarSign,
  SendHorizonal,
  Users,
} from "lucide-react";
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from "../Components/ui/card";
import { Input } from "../Components/ui/input";
import { Label } from "../Components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../Components/ui/select";
import { toast, useToast } from "../Components/ui/use-toast";
import { Toaster } from "../Components/ui/toaster";

import { Area, AreaChart, CartesianGrid, XAxis } from "recharts";

import {
  ChartConfig,
  ChartContainer,
  ChartLegend,
  ChartLegendContent,
  ChartTooltip,
  ChartTooltipContent,
} from "../Components/ui/chart";
import { CalendarIcon, PaperPlaneIcon } from "@radix-ui/react-icons";
import { addDays, format, subDays } from "date-fns";
import { DateRange } from "react-day-picker";

import { cn } from "../lib/utils";
import { Calendar } from "../Components/ui/calendar";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "../Components/ui/popover";
import axios from "axios";
import { Skeleton } from "../Components/ui/skeleton";
import config from "../config.json";
import { useSelector, useDispatch } from "react-redux";
import { RootState } from "../State/store";
import { stat } from "fs";
import CircularProgress from "@mui/material/CircularProgress";
import { setPermissions, setUser_Role_Name, setSentCount, setTotalAvailableCount } from "../State/slices/AdvertiserAccountSlice";


interface Transactions {
  paymentId: string;
  amount: string;
  paymentDate: string;
  symbol: string;
  receipturl: string;
  name: string;
  messages: string;
  fundtype: string;
}


// Updated interface to match API response
interface ChartData {
  date: string;
  SMS?: number;
  WhatsApp?: number;
  MTN?: number;
  // Keep legacy support
  Mtn?: number;
  Sms?: number;
  pushNotifications?: number;
  rcSmessages?: number;
  Whatsapp?: number;
}

interface DatePickerWithRangeProps
  extends React.HTMLAttributes<HTMLDivElement> {
  setChartData: (data: any) => void;
  fetchData: () => void;
  setMessageSentDescription: React.Dispatch<React.SetStateAction<string>>;
  setDate: React.Dispatch<React.SetStateAction<DateRange | undefined>>;
  date: DateRange | undefined;
  setSelectedView: React.Dispatch<React.SetStateAction<string>>;
  setBefore30DaysData: React.Dispatch<React.SetStateAction<any[]>>;
  setLast30DaysData: React.Dispatch<React.SetStateAction<any[]>>;
}

export function DatePickerWithRange({
  className,
  setChartData,
  setMessageSentDescription,
  setSelectedView,
  date,
  setDate,
  setBefore30DaysData,
  setLast30DaysData
}: DatePickerWithRangeProps) {

  const dispatch = useDispatch();
  const toast = useToast();
  const Workspace_Id = useSelector(
    (state: RootState) => state.authentication.workspace_id
  );
  const apiUrlAdvAcc = useSelector(
    (state: RootState) => state.authentication.apiURL
  );

  useEffect(() => {
    console.log("The date is", date);
    if (date && date.from && date.to) {
      const date_from = format(date.from, "yyyy-MM-dd");
      const date_to = format(date.to, "yyyy-MM-dd");

      const before30_from = "2025-08-01";
      const before30_to = format(subDays(new Date(date_from), 1), "yyyy-MM-dd");

      const ChartDateRange = async () => {
        try {
          const [last30Days, before30Days] = await Promise.all([
            axios.get(`${apiUrlAdvAcc}/GetCombinedStatisticsByDateRange?workspaceId=${Workspace_Id}&from_date=${date_from.toString()}&to_date=${date_to.toString()}`),
            axios.get(`${apiUrlAdvAcc}/GetCombinedStatisticsByDateRange?workspaceId=${Workspace_Id}&from_date=${before30_from.toString()}&to_date=${before30_to.toString()}`)
          ]);

          if (
            last30Days.data.status === "Success" &&
            last30Days.data.chartDetails.length > 0
          ) {
            setLast30DaysData(last30Days.data.status === "Success" ? last30Days.data : 0);
            setBefore30DaysData(before30Days.data.status === "Success" ? before30Days.data : 0);
            setChartData(last30Days.data);
          } else {
            console.error("chart details not found");
          }
        } catch (error) {
          console.error("error in fetching chart details: ", error);
        }
      };

      ChartDateRange();
    } else {
      console.log("No date selected");
    }
  }, [date]);

  useEffect(() => {
    if (date?.from && date?.to) {
      setSelectedView("none");
    }
  }, [date, setSelectedView]);

  return (
    <div className={cn("flex justify-end gap-2 pb-4 ", className)}>
      <Popover>
        <PopoverTrigger asChild>
          <Button
            id="date"
            variant={"outline"}
            className={cn(
              "min-w-[150px] w-auto justify-start text-left font-normal border-E2E8F0",
              !date && "text-muted-foreground text-[#020617] "
            )}
          >
            <CalendarIcon
              className="mr-2 h-4 w-4"
              style={{ color: "#020617" }}
            />
            {date?.from ? (
              date.to ? (
                <>
                  {format(date.from, "LLL dd, y")} -{" "}
                  {format(date.to, "LLL dd, y")}
                </>
              ) : (
                format(date.from, "LLL dd, y")
              )
            ) : (
              <span>Pick a date</span>
            )}
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-auto p-0" align="start">
          <Calendar
            initialFocus
            mode="range"
            defaultMonth={date?.from}
            selected={date}
            onSelect={(newDate) => {
              setDate(newDate);

              if (newDate?.from && newDate?.to) {
                const formattedFrom = format(newDate.from, "MMM dd, yyyy");
                const formattedTo = format(newDate.to, "MMM dd, yyyy");

                setMessageSentDescription(`Showing total messages sent from ${formattedFrom} to ${formattedTo}`);
                setSelectedView("none");
              }
            }}
            numberOfMonths={1}
          />
        </PopoverContent>
      </Popover>
    </div>
  );
}

interface DashChartProps {
  Data: ChartData[];
  setTimeRange: React.Dispatch<React.SetStateAction<string>>;
  timeRange: string;
  isWeek: boolean;
  messageSentDescription: string;
  dynamicChartConfig: ChartConfig;
  channels: string[];
}

const DashChart: FC<DashChartProps> = ({
  Data,
  setTimeRange,
  timeRange,
  isWeek,
  messageSentDescription,
  dynamicChartConfig,
  channels
}) => {
  const chartData = Data.map((item) => {
    const date = new Date(item.date).toLocaleDateString("en-US", {
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
    });

    return {
      ...item,
      date,
    };
  });

  const filteredData = chartData.filter((item) => {
    const date = new Date(item.date);
    const referenceDate = new Date("2024-06-30");
    let daysToSubtract = 90;
    if (timeRange === "30d") {
      daysToSubtract = 30;
    } else if (timeRange === "7d") {
      daysToSubtract = 7;
    }
    const startDate = new Date(referenceDate);
    startDate.setDate(startDate.getDate() - daysToSubtract);
    return date >= startDate;
  });

  // Function to get the API field name from channel name
  const getApiFieldName = (channelName: string): string => {
    switch (channelName.toLowerCase()) {
      case 'sms':
        return 'SMS';
      case 'whatsapp':
        return 'WhatsApp';
      case 'mtn':
        return 'MTN';
      default:
        return channelName;
    }
  };

  return (
    <Card className="mt-[20px] w-[100%] h-fit relative mb-[100px] overflow-y-auto no-scrollbar">
      <CardHeader className="flex items-center gap-2 space-y-0 border-b py-5 sm:flex-row">
        <div className="grid flex-1 gap-1 text-center sm:text-left">
          <CardTitle>Sent messages</CardTitle>
          <CardDescription>
            {messageSentDescription}
          </CardDescription>
        </div>
      </CardHeader>
      <CardContent className="px-2 pt-4 sm:px-6 sm:pt-6">
        <ChartContainer config={dynamicChartConfig} className="aspect-auto h-[200px] w-full">
          <AreaChart data={filteredData}>
            <defs>
              {/* SMS Gradient */}
              <linearGradient id="fillSMS" x1="0" y1="0" x2="0" y2="1">
                <stop
                  offset="5%"
                  stopColor="hsl(var(--chart-2))"
                  stopOpacity={0.8}
                />
                <stop
                  offset="95%"
                  stopColor="hsl(var(--chart-2))"
                  stopOpacity={0.1}
                />
              </linearGradient>

              {/* WhatsApp Gradient */}
              <linearGradient id="fillWhatsApp" x1="0" y1="0" x2="0" y2="1">
                <stop
                  offset="5%"
                  stopColor="hsl(var(--chart-5))"
                  stopOpacity={0.8}
                />
                <stop
                  offset="95%"
                  stopColor="hsl(var(--chart-5))"
                  stopOpacity={1.0}
                />
              </linearGradient>

              {/* MTN Gradient */}
              <linearGradient id="fillMTN" x1="0" y1="0" x2="0" y2="1">
                <stop
                  offset="5%"
                  stopColor="hsl(var(--chart-3))"
                  stopOpacity={0.8}
                />
                <stop
                  offset="95%"
                  stopColor="hsl(var(--chart-3))"
                  stopOpacity={0.1}
                />
              </linearGradient>
            </defs>
            <CartesianGrid vertical={false} />
            <XAxis
              dataKey="date"
              tickLine={false}
              axisLine={false}
              tickMargin={8}
              minTickGap={32}
              tickFormatter={(value) => {
                const date = new Date(value);
                return date.toLocaleDateString("en-US", {
                  month: "short",
                  day: "numeric",
                });
              }}
            />
            <ChartTooltip
              cursor={false}
              content={
                <ChartTooltipContent
                  className="min-w-[200px] w-auto text-left"
                  labelFormatter={(value) => {
                    return new Date(value).toLocaleDateString("en-US", {
                      year: "numeric", // ✅ Added year
                      month: "long",   // ✅ Changed to full month name
                      day: "numeric",
                    })
                  }}
                  indicator="dot"
                />
              }
            />
            {[...channels]
              .sort((a, b) => {
                // Keep a consistent order: WhatsApp, SMS, then MTN
                const order = ["WhatsApp", "SMS", "MTN"];
                return order.indexOf(a) - order.indexOf(b);
              })
              .map((name,i) => (
                <Area
                  key={name}
                  dataKey={name}
                  type="monotone"
                  fill={`url(#fill${name})`}
                  stroke={
                    name === "WhatsApp"
                      ? "hsl(var(--chart-5))"
                      : name === "SMS"
                        ? "hsl(var(--chart-2))"
                        : name === "MTN"
                          ? "hsl(var(--chart-3))"
                          : "hsl(var(--chart-1))"
                  }
                  stackId={i+1}
                />
              ))}

            <ChartLegend content={<ChartLegendContent />} />
          </AreaChart>
        </ChartContainer>
      </CardContent>
    </Card>
  );
};

interface CardProps {
  title: string;
  value: string | number;
  change: string;
  icon?: ReactNode;
}

const CardComponent: FC<CardProps> = ({ title, value, change, icon }) => {
  return (
    <Card className="w-full md:w-[200px] lg:w-[220px] xl:w-[240px] h-fit relative flex-grow border-[#E2E8F0]">
      <div className="p-1 pl-0 pr-0">
        {icon && (
          <div className="absolute top-7 right-6 text-gray-400">{icon}</div>
        )}

        <CardHeader className="text-left pb-2">
          <CardTitle className="text-[14px] text-[#020617] font-medium leading-[20px] text-left">
            {title}
          </CardTitle>
        </CardHeader>
        <CardContent className="text-left text-[#020617] text-2xl font-bold leading-[24px] mt-1">
          {value}
          <div className="text-[12px] text-[#64748B] font-normal leading-[20px] mt-[2px]">
            {change}
          </div>
        </CardContent>
      </div>
    </Card>
  );
};

const Dashboard: FC = () => {
  const [selectedView, setSelectedView] = useState<string>("month");
  const [date, setDate] = useState<DateRange | undefined>();
  const [chartData, setChartData] = useState<any>();
  // ✅ Channels come from API data only (MTN if present; else WhatsApp/SMS subset)
  const apiChannels = React.useMemo<string[]>(() => {
    if (!chartData?.chartDetails?.length) return [];
    const present = new Set<string>();

    // Collect known keys that appear as numbers
    for (const row of chartData.chartDetails as Array<Record<string, any>>) {
      ["MTN", "WhatsApp", "SMS"].forEach((k) => {
        if (typeof row[k] === "number") present.add(k);
      });
    }

    if (present.has("MTN")) {
      // MTN mode: show everything that exists among MTN/WhatsApp/SMS
      return ["MTN", "WhatsApp", "SMS"].filter((k) => present.has(k));
    }
    // No MTN: show only WhatsApp/SMS when present
    return ["WhatsApp", "SMS"].filter((k) => present.has(k));
  }, [chartData]);

  const [isLoading, setIsLoading] = useState(true);

  const [messageSentDescription, setMessageSentDescription] = useState<string>(
    "Showing total messages sent per month"
  );

  const [isWeek, setIsWeek] = useState(false);
  const [isMonth, setIsMonth] = useState(false);
  const [timeRange, setTimeRange] = React.useState("30d");
  const [last30DaysData, setLast30DaysData] = useState<any>(null);
  const [before30DaysData, setBefore30DaysData] = useState<any>(null);

  const [date_Week, setDate_Week] = React.useState<DateRange | undefined>({
    from: subDays(new Date(), 7),
    to: new Date(),
  });
  const [date_Month, setDate_Month] = React.useState<DateRange | undefined>({
    from: subDays(new Date(), 30),
    to: new Date(),
  });

  const Workspace_Id = useSelector(
    (state: RootState) => state.authentication.workspace_id
  );
  const EmailId = useSelector(
    (state: RootState) => state.authentication.userEmail
  );
  const isInvited = useSelector(
    (state: RootState) => state.authentication.isInvited
  );

  const [sentMessages, setSentMessages] = useState<number>(0);
  const [totalPurchasedMessages, setTotalPurchasedMessages] = useState<number>(0);

  const accountId = useSelector((state: RootState) => state.authentication.account_id);
  const apiUrlAdvAcc = useSelector((state: RootState) => state.authentication.apiURL);
  const apiUrl = useSelector((state: RootState) => state.authentication.authURL);
  const roleId = useSelector((state: RootState) => state.authentication.role_id);
  console.log(Workspace_Id);
  const dispatch = useDispatch();

  useEffect(() => {
    if (apiUrlAdvAcc) {
      GetPermissionsByRoleId();
    }
  }, [apiUrlAdvAcc, Workspace_Id]);

  useEffect(() => {
    setIsLoading(true);
    setSelectedView("month");
    setIsMonth(true);
    setIsWeek(false);
    setTimeRange("30d");
    setDate(undefined);
    ByMonthData();
    fetchPast30DaysData();
    setMessageSentDescription("Showing total messages sent per month");
  }, [Workspace_Id]);

  const GetPermissionsByRoleId = async () => {
    debugger
    const response2 = await axios.get(`${apiUrl}/GetPermissionsByRoleId?RoleID=${roleId}`);
    if (response2.data.status === "Success") {
      const permissions = JSON.parse(response2.data.roleDetails.permissions);
      const role_name = response2.data.roleDetails.roleName;
      dispatch(setPermissions(permissions));
      dispatch(setUser_Role_Name(role_name));
    } else {
      console.log("GetPermissionsByRoleId API error");
    }
  }

  const usertransactionlist = async () => {
    try {
      const response = await axios.get(`${apiUrlAdvAcc}/GetuserTransaction?accountid=${accountId}`);
      console.log("Response : ", response.data.user_transaction);
      const data = response.data.user_transaction;
      if (response.data.status === "Success") {
        const creditMessageCount = Array.isArray(data)
          ? data.reduce((sum, item) => sum + Number(item.messages || 0), 0)
          : 0;
        console.log("CMC:    ", creditMessageCount);
        dispatch(setTotalAvailableCount(creditMessageCount));
      }
      else {
        console.error("error in retrieving user_transaction list or list does not exist")
      }
    } catch (error) {
      console.error("Error fetching Transaction details:", error);
    }
  }

  const ByWeekData = async () => {
    if (date_Week && date_Week.from && date_Week.to) {
      const date_from = format(date_Week.from, "yyyy-MM-dd");
      const date_to = format(date_Week.to, "yyyy-MM-dd");

      const ChartDateRange = async () => {
        try {
          setIsLoading(true);
          const response = await axios.get(
            `${apiUrlAdvAcc}/GetCombinedStatisticsByDateRange?workspaceId=${Workspace_Id}&from_date=${date_from.toString()}&to_date=${date_to.toString()}`
          );
          console.log("ByWeek API Response:", response.data);
          if (
            response.data.status === "Success" &&
            response.data.chartDetails.length > 0
          ) {
            setChartData(response.data);
          } else {
            console.error("chart details not found");
          }
        } catch (error) {
          console.error("error in fetching chart details: ", error);
        } finally {
          setIsLoading(false);
        }
      };

      ChartDateRange();
    }
  };

  const ByMonthData = async () => {
    if (date_Month && date_Month.from && date_Month.to) {
      const date_from = format(date_Month.from, "yyyy-MM-dd");
      const date_to = format(date_Month.to, "yyyy-MM-dd");

      const ChartDateRange = async () => {
        try {
          setIsLoading(true);
          const response = await axios.get(
            `${apiUrlAdvAcc}/GetCombinedStatisticsByDateRange?workspaceId=${Workspace_Id}&from_date=${date_from.toString()}&to_date=${date_to.toString()}`
          );
          console.log("ByMonth API Response:", response.data.chartDetails);
          if (
            response.data.status === "Success" &&
            response.data.chartDetails.length > 0
          ) {
            setChartData(response.data);
          } else {
            console.error("chart details not found");
          }
        } catch (error) {
          console.error("error in fetching chart details: ", error);
        } finally {
          setIsLoading(false);
        }
      };

      ChartDateRange();
    }
  };

  const fetchPast7DaysData = async () => {
    if (!apiUrlAdvAcc || !Workspace_Id) return;

    const past_from = format(subDays(new Date(), 7), "yyyy-MM-dd");
    const past_to = format(new Date(), "yyyy-MM-dd");

    const before7_from = format(new Date("2024-01-01"), "yyyy-MM-dd");
    const before7_to = format(subDays(new Date(), 7), "yyyy-MM-dd");

    try {
      setIsLoading(true);
      const [last7Days, before7Days] = await Promise.all([
        axios.get(`${apiUrlAdvAcc}/GetCombinedStatisticsByDateRange?workspaceId=${Workspace_Id}&from_date=${past_from}&to_date=${past_to}`),
        axios.get(`${apiUrlAdvAcc}/GetCombinedStatisticsByDateRange?workspaceId=${Workspace_Id}&from_date=${before7_from}&to_date=${before7_to}`)
      ]);

      setLast30DaysData(last7Days.data.status === "Success" ? last7Days.data : null);
      setBefore30DaysData(before7Days.data.status === "Success" ? before7Days.data : null);
    } catch (error) {
      console.error("Error fetching past 7 days data:", error);
    }
  };

  const fetchPast30DaysData = async () => {
    if (!apiUrlAdvAcc || !Workspace_Id) return;

    const past_from = format(subDays(new Date(), 30), "yyyy-MM-dd");
    const past_to = format(new Date(), "yyyy-MM-dd");

    const before30_from = format(new Date("2024-01-01"), "yyyy-MM-dd");
    const before30_to = format(subDays(new Date(), 30), "yyyy-MM-dd");

    try {
      const [last30Days, before30Days] = await Promise.all([
        axios.get(`${apiUrlAdvAcc}/GetCombinedStatisticsByDateRange?workspaceId=${Workspace_Id}&from_date=${past_from}&to_date=${past_to}`),
        axios.get(`${apiUrlAdvAcc}/GetCombinedStatisticsByDateRange?workspaceId=${Workspace_Id}&from_date=${before30_from}&to_date=${before30_to}`)
      ]);

      setLast30DaysData(last30Days.data.status === "Success" ? last30Days.data : null);
      setBefore30DaysData(before30Days.data.status === "Success" ? before30Days.data : null);

    } catch (error) {
      console.error("Error fetching past 30 days data:", error);
    } finally {
      setIsLoading(false);
    }
  };

  // Update the dynamic chart config to use API field names
  // ✅ Build ChartConfig purely from apiChannels
  const dynamicChartConfig: ChartConfig = React.useMemo(() => {
    return apiChannels.reduce((cfg, name, idx) => {
      cfg[name] = {
        label: name,
        color: `hsl(var(--chart-${idx + 2}))`,
      };
      return cfg;
    }, {} as ChartConfig);
  }, [apiChannels]);


  useEffect(() => {
    setChartData(null);
    setIsLoading(true);
    fetchPast30DaysData();
  }, [apiUrlAdvAcc, Workspace_Id]);

  const calculatePercentageChange = (
    last30Days: number,
    before30Days: number,
    date?: DateRange,
    timeRange?: string
  ) => {
    let type = "last month";

    if (date && date.from && date.to) {
      type = "selected date range";
    } else {
      if (timeRange === "7d") {
        type = "last week";
      } else if (timeRange === "30d") {
        type = "last month";
      }
    }
    if (!last30Days || last30Days === 0) return `+0% change for ${type}`;
    if (!before30Days || before30Days === 0) return `+0% change for ${type}`;

    const change = (last30Days / before30Days) * 100;
    return `${change > 0 ? "+" : ""}${change.toFixed(1)}% change for ${type}`;
  };

  const handleGetMessagesCount = async () => {
    setIsLoading(true);
    try {
      const response = await axios.get(`${apiUrlAdvAcc}/GetDebitandCreditMessagesCount?accountId=${accountId}`);
      console.log("Response : ", response.data);

      if (response.data.status === "Success" && response.data.messages_details.length > 0) {
        const messageDetails = response.data.messages_details[0];
        setSentMessages(messageDetails.sent_messages);
        setTotalPurchasedMessages(messageDetails.total_purchased_messages);
        dispatch(setSentCount(messageDetails.sent_messages));
        dispatch(setTotalAvailableCount(messageDetails.total_purchased_messages));
      } else {
        console.error("Error fetching messages count: response - ", response);
        setSentMessages(0);
        setTotalPurchasedMessages(0);
        dispatch(setSentCount(0));
        dispatch(setTotalAvailableCount(0));
      }
    } catch (error) {
      console.error("Error fetching messages count:", error);
      setSentMessages(0);
      setTotalPurchasedMessages(0);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    setIsMonth(true);
    setTimeRange("30d");
    ByMonthData();
    fetchPast30DaysData();

    if (apiUrlAdvAcc && Workspace_Id) {
      fetchPast30DaysData();
    }
  }, [apiUrlAdvAcc, Workspace_Id]);

  useEffect(() => {
    if (selectedView === "week" || selectedView === "month") {
      setDate(undefined);
    }
  }, [selectedView]);

  useEffect(() => {
    // handleGetMessagesCount();
  }, [Workspace_Id]);

  return chartData ? (
    <div className="flex-col w-full">
      <Toaster />
      <div className="flex mt-[-15px] justify-end gap-2">
        <div>
          <DatePickerWithRange
            setChartData={setChartData}
            fetchData={ByMonthData}
            setMessageSentDescription={setMessageSentDescription}
            setDate={setDate}
            date={date}
            setSelectedView={setSelectedView}
            setBefore30DaysData={setBefore30DaysData}
            setLast30DaysData={setLast30DaysData}
          />
        </div>
        <div>
          <Select
            value={selectedView}
            defaultValue="month"
            onValueChange={(value) => {
              setSelectedView(value);
              if (value === "week" || value === "month") {
                setDate(undefined);
              }
              if (value === "week") {
                setIsWeek(true);
                setTimeRange("7d");
                setDate(undefined);
                ByWeekData();
                fetchPast7DaysData();
                setMessageSentDescription("Showing total messages sent per week");
              }
              else if (value === "month") {
                setIsMonth(true);
                setTimeRange("30d");
                setDate(undefined);
                ByMonthData();
                fetchPast30DaysData();
                setMessageSentDescription("Showing total messages sent per month");
              }
              else {
                setIsWeek(false);
                setIsMonth(false);
                setTimeRange("90d");
              }
            }}
          >
            <SelectTrigger className="w-[120px] h-9 text-[#020617] mt-6">
              <SelectValue placeholder="Select view" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem className="cursor-pointer" value="week">By Week</SelectItem>
              <SelectItem className="cursor-pointer" value="month">By Month</SelectItem>
              <SelectItem className="cursor-pointer" value="none">None</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      <div className="flex flex-wrap gap-4 w-full justify-between border-orange-600">
        <CardComponent
          title="Campaigns"
          value={chartData?.campaignDetails[0]?.totalCampaigns || "0"}
          change={
            last30DaysData?.campaignDetails && before30DaysData?.campaignDetails
              ? calculatePercentageChange(
                last30DaysData?.campaignDetails[0]?.totalCampaigns,
                before30DaysData?.campaignDetails[0]?.totalCampaigns,
                date,
                timeRange
              )
              : "+0% from last month"
          }
          icon={<PaperPlaneIcon className="text-[#64748B] size-4" />}
        />

        <CardComponent
          title="Recipients"
          value={chartData?.recipientCount[0]?.recipients || "0"}
          change={
            last30DaysData?.recipientCount && before30DaysData?.recipientCount
              ? calculatePercentageChange(
                last30DaysData?.recipientCount[0]?.recipients,
                before30DaysData?.recipientCount[0]?.recipients,
                date,
                timeRange
              )
              : "+0% from last month"
          }
          icon={<Users className="text-[#64748B]" size={16} />}
        />
        <CardComponent
          title="Sent"
          value={chartData?.messagesSentDetails[0]?.totalSent || 0}
          change={
            last30DaysData?.messagesSentDetails && before30DaysData?.messagesSentDetails
              ? calculatePercentageChange(
                last30DaysData?.messagesSentDetails[0]?.totalSent,
                before30DaysData?.messagesSentDetails[0]?.totalSent,
                date,
                timeRange
              )
              : "+0% from last month"
          }
          icon={<Check className="text-[#64748B]" size={16} />}
        />
        <CardComponent
          title="Delivery rate"
          value={
            (() => {
              const sent = chartData?.messagesSentDetails?.[0]?.totalSent ?? 0;
              const recipients = chartData?.recipientCount?.[0]?.recipients ?? 0;
              const pct = recipients > 0 ? (sent / recipients) * 100 : 0;
              return `${pct.toFixed(2)}%`;
            })()
          }

          change={
            last30DaysData?.messagesSentDetails && before30DaysData?.messagesSentDetails
              ? calculatePercentageChange(
                (last30DaysData?.messagesSentDetails[0]?.totalSent / last30DaysData?.recipientCount[0]?.recipients),
                (before30DaysData?.messagesSentDetails[0]?.totalSent / before30DaysData?.recipientCount[0]?.recipients),
                date,
                timeRange
              )
              : "+0% from last month"
          }
          icon={<Activity className="text-[#64748B]" size={16} />}
        />
      </div>

      <DashChart
        Data={chartData.chartDetails}
        setTimeRange={setTimeRange}
        timeRange={timeRange}
        isWeek={isWeek}
        messageSentDescription={messageSentDescription}
        dynamicChartConfig={dynamicChartConfig}
        channels={apiChannels}
      />
    </div>
  ) : (
    <div>
      {isLoading && (
        <div className="flex flex-col items-center justify-center h-[500px]">
          <CircularProgress color="primary" />
        </div>
      )}
    </div>
  );
};

export default Dashboard;